import { useState, useCallback, useEffect, Fragment, FC, memo } from 'react'
import { useRouter } from 'next/router'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { QuizState } from '~/bootcamp/components/Quiz/QuizData.models'
import { useGetBootcampBossQuery } from '~/bootcamp/apis/get-boss.api'
import { useEnergyOrbsFromBoss } from '~/bootcamp/hooks/useWillGetEnergyOrbs'
import ROUTES from '~/shared/constants/routes.const'
import bootcampAnalytics from '~/analytics/bootcamp/bootcampAnalytics'
import {
  setQuizState,
  selectQuizState,
  setStartedQuiz,
  setBossCurrentBehavior,
  setMathEnergyEarned,
  setBossBattleEnemy,
  selectHasStartedQuiz,
  resetQuizStore,
} from '~/bootcamp/slices/bootcamp-quiz.slice'

import { BossBehaviors } from '~/bootcamp/Activities/types/activities.types'
import { BCAEvent } from '~/analytics/bootcamp/bootcampEvents.types'

import { QuizWrapperChildTypes, QuizWrapperProps } from '~/bootcamp/components/Quiz/QuizProps.models'

import QuizFailedScreen from './QuizFailedScreen'
import QuizQuestionCounterAndTimer from './QuizQuestionCounterAndTimer'
import QuizQuestionsWrapper from './QuizQuestionsWrapper'
import QuizResult from './QuizResult'

const QuizWrapper: FC<QuizWrapperProps> = ({ bootcampKey, subtopic, moduleKey, isCompleted }) => {
  const [childState, setChildState] = useState<QuizWrapperChildTypes>('intro')
  const [quizCurrentState, setQuizCurrentState] = useState<QuizState | null>(null)
  const [quizInitialState, setQuizInitialState] = useState<QuizState | null>(null)

  const router = useRouter()
  const { data: quizQuestionConfig, isLoading } = useGetBootcampBossQuery({
    language: String(router.locale),
    key: bootcampKey as string,
    module: moduleKey as string,
  })

  const taskId = quizQuestionConfig?.taskId
  const quizState = useAppSelector<QuizState>(selectQuizState)
  const energyOrbs = useEnergyOrbsFromBoss(isCompleted)
  const dispatch = useAppDispatch()
  const hasStartedQuiz = useAppSelector(selectHasStartedQuiz)
  const getTimeNowWithoutTimeZone = useCallback(() => {
    const timeNow = new Date()
    const createTimeOffset = timeNow.getTimezoneOffset() * 60000
    return new Date(timeNow.getTime() - createTimeOffset).toISOString()
  }, [])

  useEffect(() => {
    if (!quizQuestionConfig) return
    dispatch(setBossBattleEnemy(quizQuestionConfig.bossBattleEnemy))
  }, [quizQuestionConfig, dispatch])

  const initializeQuizState = useCallback((): void => {
    setQuizInitialState({
      bootcampKey,
      subtopic,
      startTime: getTimeNowWithoutTimeZone(),
      // endTime: undefined,
      totalQuestions: quizQuestionConfig?.questions.length ?? 0,
      completedQuestions: 0,
      bossHealth: 100,
      damagePlayer: false,
      wrongAnswer: 0,
      maxWrongAnswer: quizQuestionConfig?.maxWrongAnswer ?? 0,
      questions: quizQuestionConfig?.questions.map((q) => ({ pass: null, activity: { ...q } })) ?? [],
      badgeImage: quizQuestionConfig?.badgeImage ?? { srcKey: '', alt: '' },
      bossBattleEnemy: quizQuestionConfig?.bossBattleEnemy ?? {
        avatarGLBFilePath: '',
        avatarImagePath: {
          alt: '',
          srcKey: '',
        },
        dragonId: 0,
      },
    })

    setQuizCurrentState(null)
  }, [quizQuestionConfig, setQuizInitialState, getTimeNowWithoutTimeZone, bootcampKey, subtopic])

  const renderIntro = useCallback(() => {
    dispatch(setBossCurrentBehavior(BossBehaviors.RELOAD))
    setChildState('intro')
    bootcampAnalytics.log(BCAEvent.BOOS_FIGHT_QUIT, { category: subtopic, subject: bootcampKey, bootcamp: moduleKey })
  }, [dispatch, subtopic, bootcampKey, moduleKey])

  const handleStartQuiz = useCallback(() => {
    initializeQuizState()
    dispatch(setStartedQuiz(true))
    dispatch(setMathEnergyEarned(energyOrbs))
    setChildState('questions')
    bootcampAnalytics.log(BCAEvent.BOSS_FIGHT_STARTED, {
      category: subtopic,
      subject: bootcampKey,
      bootcamp: moduleKey,
    })
  }, [initializeQuizState, dispatch, energyOrbs, subtopic, bootcampKey, moduleKey])

  useEffect(() => {
    if (childState === 'intro' && hasStartedQuiz) {
      handleStartQuiz()
    }
  }, [childState, hasStartedQuiz, handleStartQuiz])

  const handleRestartQuiz = useCallback(() => {
    initializeQuizState()
    dispatch(setMathEnergyEarned(energyOrbs))
    dispatch(setBossCurrentBehavior(BossBehaviors.RESTART))
    setChildState('questions')
    bootcampAnalytics.log(BCAEvent.BOSS_FIGHT_RESTARTED, {
      category: subtopic,
      subject: bootcampKey,
      bootcamp: moduleKey,
    })
  }, [initializeQuizState, dispatch, energyOrbs, subtopic, bootcampKey, moduleKey])

  const handleQuizFail = useCallback(() => {
    setChildState('quizFailed')
    bootcampAnalytics.log(BCAEvent.LOST_BOSS_BATTLE, {
      category: subtopic,
      subject: bootcampKey,
      bootcamp: moduleKey,
    })
  }, [bootcampKey, moduleKey, subtopic])

  const handleVictory = useCallback(() => {
    setQuizCurrentState((prevState) => {
      if (!prevState) return null
      return { ...prevState, endTime: getTimeNowWithoutTimeZone() }
    })
    setChildState('result')
    bootcampAnalytics.log(BCAEvent.WIN_BOSS_BATTLE, {
      category: subtopic,
      subject: bootcampKey,
      bootcamp: moduleKey,
    })
  }, [subtopic, bootcampKey, moduleKey, getTimeNowWithoutTimeZone])

  const handleQuizEnd = useCallback(() => {
    router.push({
      pathname: ROUTES.BOOTCAMP_MODULE_OVERVIEW,
      query: {
        subTopic: subtopic,
        key: bootcampKey,
      },
    })
  }, [bootcampKey, router, subtopic])

  useEffect(() => {
    const newSate = quizCurrentState || quizInitialState
    if (newSate) dispatch(setQuizState(newSate))
  }, [quizCurrentState, quizInitialState, dispatch])

  useEffect(() => {
    return () => {
      dispatch(resetQuizStore())
      // Hide bootcamp boss
      dispatch(setStartedQuiz(false))

      // Reset boss behavior
      dispatch(setBossCurrentBehavior(null))
    }
  }, [dispatch])

  const questionsComponent = (): React.ReactElement => {
    if (isLoading || !quizInitialState) return <>Loading...</>
    return (
      <>
        <QuizQuestionCounterAndTimer
          cancelQuiz={renderIntro}
          current={
            quizCurrentState ? Math.min(quizCurrentState.completedQuestions + 1, quizCurrentState.totalQuestions) : 1
          }
          total={quizInitialState.totalQuestions}
        />
        <QuizQuestionsWrapper
          onFail={handleQuizFail}
          onQuit={handleVictory}
          onQuizStateUpdate={setQuizCurrentState}
          quizInitialState={quizInitialState}
        />
      </>
    )
  }

  return (
    <Fragment>
      {childState === 'questions' && quizInitialState && questionsComponent()}
      {childState === 'quizFailed' && (
        <QuizFailedScreen
          correctAnswer={quizState.completedQuestions - quizState.wrongAnswer}
          onQuit={handleRestartQuiz}
          totalQuestion={quizState.totalQuestions}
        />
      )}
      {childState === 'result' && quizState && (
        <QuizResult
          badgeImage={quizState.badgeImage}
          correctAnswer={quizState.completedQuestions - quizState.wrongAnswer}
          mathEnergyEarned={quizState.mathEnergyEarned}
          onQuit={handleQuizEnd}
          taskId={taskId}
          totalQuestion={quizState.totalQuestions}
        />
      )}
      {childState === 'intro' && <span data-testid="quiz-page-content" />}
    </Fragment>
  )
}

export default memo(QuizWrapper)
